import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isSupervisorOrAbove } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowsLeftRight, Check, X, Handshake } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

function fmtDateTime(iso) {
  const d = new Date(iso);
  return `${d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * PendingTradesPanel
 * -------------------
 * Every authenticated member can view. Cards fall into three flavours:
 *   • Two-sided trade (`a` + `b`) — poster and partner both known.
 *   • Open trade (`a` only) — no partner yet. Any eligible member can
 *     tap "Claim this trade" to offer one of their own shifts.
 *   • Poster viewing their own open trade — can cancel with one tap.
 * Supervisor+ additionally sees Approve / Deny on two-sided trades.
 */
export default function PendingTradesPanel() {
  const { user } = useAuth();
  const canDecide = isSupervisorOrAbove(user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claimTrade, setClaimTrade] = useState(null); // trade being claimed

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/tours/trade-requests");
      setRows(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load trades");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function approve(req) {
    try {
      await api.post(`/tours/${req.a.id}/trade-approve`);
      toast.success("Trade approved — assignments swapped");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approval failed"); }
  }
  async function deny(req) {
    try {
      await api.post(`/tours/${req.a.id}/trade-deny`);
      toast.success("Trade denied");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Deny failed"); }
  }
  async function cancelOwn(req) {
    if (!window.confirm("Cancel this trade offer? It will be removed from the board.")) return;
    try {
      await api.post(`/tours/trades/${req.trade.id}/cancel`);
      toast.success("Trade cancelled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  }

  return (
    <div className="space-y-4" data-testid="pending-trades-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          Trade requests across the corps. Open trades (no partner yet) can be claimed by any eligible member; supervisors approve two-sided trades.
        </p>
        <span className="tab-tag text-slate-500" data-testid="trades-count">{rows.length} pending</span>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 italic p-6 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="surface-grid p-8 text-center text-sm text-slate-500" data-testid="trades-empty">
          <ArrowsLeftRight size={28} weight="bold" className="mx-auto mb-2 text-slate-400" />
          No pending trades.
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map(({ trade, a, b }) => {
            const involvesMe = user && (trade.requested_by === user.id || trade.target_user_id === user.id);
            const isOpen = !b;
            const iAmPoster = user && trade.requested_by === user.id;
            return (
              <div key={trade.id} className={`surface-grid p-4 ${involvesMe ? "ring-2 ring-emerald-500" : ""}`} data-testid={`trade-${trade.id}`}>
                <div className="flex items-center justify-between border-b border-slate-200 pb-2 mb-3">
                  <div>
                    <div className="font-display font-bold text-sm flex items-center gap-2">
                      {isOpen ? (
                        <>
                          <span>{trade.requested_by_name}</span>
                          <span className="text-slate-400">↔</span>
                          <Badge className="rounded-sm bg-blue-600 text-white text-[10px]">OPEN — awaiting claim</Badge>
                        </>
                      ) : (
                        <>{trade.requested_by_name} <span className="text-slate-400">↔</span> {trade.target_user_name}</>
                      )}
                      {involvesMe && <span className="ml-2 text-emerald-700 text-[10px] font-bold tracking-wider">★ YOU'RE INVOLVED</span>}
                    </div>
                    <div className="text-[11px] text-slate-500 mono">
                      Requested {new Date(trade.requested_at).toLocaleString()}
                    </div>
                  </div>
                  <Badge className="rounded-sm bg-amber-200 text-amber-900 text-[10px]">PENDING</Badge>
                </div>

                <div className={`grid gap-3 text-sm ${isOpen ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"}`}>
                  <ShiftCard label={`${trade.requested_by_name}'s shift`} tour={a} highlight="emerald" />
                  {b && <ShiftCard label={`${trade.target_user_name}'s shift`} tour={b} highlight="blue" />}
                </div>

                {trade.reason && (
                  <div className="mt-3 text-xs border-l-2 border-slate-400 pl-2 italic text-slate-600">
                    "{trade.reason}"
                  </div>
                )}

                {/* Actions row */}
                <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {isOpen ? (
                    <>
                      {iAmPoster ? (
                        <Button size="sm" variant="outline" onClick={() => cancelOwn({ trade })} className="rounded-sm border-red-300 text-red-700 hover:bg-red-50 md:col-span-2" data-testid={`trade-cancel-${trade.id}`}>
                          <X size={14} weight="bold" className="mr-1" />Cancel my open trade
                        </Button>
                      ) : (
                        <Button size="sm" onClick={() => setClaimTrade({ trade, a })} className="rounded-sm bg-blue-700 hover:bg-blue-800 text-white md:col-span-2" data-testid={`trade-claim-${trade.id}`}>
                          <Handshake size={14} weight="bold" className="mr-1" />Claim this trade
                        </Button>
                      )}
                    </>
                  ) : canDecide ? (
                    <>
                      <Button size="sm" onClick={() => approve({ trade, a, b })} className="rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white" data-testid={`trade-approve-${trade.id}`}>
                        <Check size={14} weight="bold" className="mr-1" />Approve Trade
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => deny({ trade, a, b })} className="rounded-sm border-red-300 text-red-700 hover:bg-red-50" data-testid={`trade-deny-${trade.id}`}>
                        <X size={14} weight="bold" className="mr-1" />Deny
                      </Button>
                    </>
                  ) : (
                    <div className="text-[11px] text-slate-500 italic text-center md:col-span-2">
                      Awaiting supervisor decision.
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ClaimDialog trade={claimTrade} onClose={() => setClaimTrade(null)} onClaimed={() => { setClaimTrade(null); load(); }} />
    </div>
  );
}

function ShiftCard({ label, tour, highlight }) {
  const ringCls = highlight === "emerald" ? "border-emerald-200 bg-emerald-50" : "border-blue-200 bg-blue-50";
  return (
    <div className={`border rounded-sm p-2 ${ringCls}`}>
      <div className="tab-tag text-slate-500 mb-1">{label}</div>
      <div className="font-semibold">{tour.position || tour.title}</div>
      <div className="text-xs text-slate-700 mono">{fmtDateTime(tour.start_time)}</div>
      <div className="flex items-center gap-1 mt-1 flex-wrap">
        <Badge className="rounded-sm text-[10px] bg-slate-700 text-white">{tour.required_certification}</Badge>
        {tour.ambulance_role && <Badge className="rounded-sm text-[10px] bg-teal-700 text-white">{tour.ambulance_role}</Badge>}
      </div>
    </div>
  );
}

/** Modal for claiming an open trade — lists the current user's own
 *  upcoming assigned shifts so they can pick one to offer in return. */
function ClaimDialog({ trade, onClose, onClaimed }) {
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!trade) return;
    setPick("");
    setLoading(true);
    api.get("/tours/mine")
      .then((r) => {
        // Only upcoming assigned shifts qualify — filter client-side.
        const now = Date.now();
        const upcoming = (r.data || []).filter((t) => new Date(t.start_time).getTime() > now);
        setMine(upcoming);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load your shifts"))
      .finally(() => setLoading(false));
  }, [trade]);

  async function submit() {
    if (!pick) { toast.error("Pick one of your shifts to offer"); return; }
    setBusy(true);
    try {
      await api.post(`/tours/trades/${trade.trade.id}/claim`, { target_tour_id: pick });
      toast.success("Claimed — supervisor will approve the swap");
      onClaimed();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Claim failed");
    } finally { setBusy(false); }
  }

  if (!trade) return null;
  const a = trade.a;
  return (
    <Dialog open={!!trade} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="claim-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Handshake size={18} weight="bold" /> Claim Open Trade
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-1">
          <section>
            <Label className="tab-tag mb-1.5 block">Trade Being Offered</Label>
            <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
              <div className="font-semibold text-sm">{a.position || a.title}</div>
              <div className="text-xs text-slate-600 mono mt-0.5">{fmtDateTime(a.start_time)}</div>
              <div className="text-[11px] text-slate-500 mt-1">by {trade.trade.requested_by_name}</div>
            </div>
          </section>
          <section>
            <Label className="tab-tag mb-1.5 block">Pick One of Your Shifts to Offer</Label>
            {loading ? (
              <div className="text-xs text-slate-500 italic p-4 text-center">Loading your shifts…</div>
            ) : mine.length === 0 ? (
              <div className="text-xs text-slate-600 p-4 text-center border border-dashed border-slate-300 rounded-sm bg-slate-50">
                You have no upcoming assigned shifts to offer.
              </div>
            ) : (
              <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                {mine.map((s) => (
                  <label
                    key={s.id}
                    className={`grid grid-cols-[auto,1fr] items-center gap-3 p-2.5 border rounded-sm cursor-pointer transition-colors ${pick === s.id ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900" : "border-slate-200 bg-white hover:bg-slate-50"}`}
                    data-testid={`claim-option-${s.id}`}
                  >
                    <input type="radio" name="claim-pick" value={s.id} checked={pick === s.id} onChange={() => setPick(s.id)} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{s.position || s.title}</span>
                        <Badge className="rounded-sm text-[10px] bg-slate-900 text-white">{s.required_certification}</Badge>
                        {s.ambulance_role && <Badge className="rounded-sm text-[10px] bg-teal-700 text-white">{s.ambulance_role}</Badge>}
                      </div>
                      <div className="text-xs text-slate-600 mono mt-0.5">{fmtDateTime(s.start_time)}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </section>
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" className="rounded-sm" onClick={onClose} data-testid="claim-cancel">Cancel</Button>
          <Button type="button" disabled={busy || !pick} onClick={submit} className="rounded-sm bg-blue-700 hover:bg-blue-800 text-white disabled:opacity-40" data-testid="claim-submit">
            {busy ? "Claiming…" : "Claim Trade"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
